import { useCallback, useEffect, useState } from "react";
import { api, errDetail, fmtEur, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ClipboardList, Plus, Trash2 } from "lucide-react";

const STATUS = {
  draft: ["bg-gray-100 text-gray-700", "Entwurf"],
  pending_approval: ["bg-amber-100 text-amber-800", "Freigabe ausstehend"],
  approved: ["bg-green-100 text-green-700", "Freigegeben"],
  rejected: ["bg-red-100 text-red-700", "Abgelehnt"],
  converted: ["bg-indigo-100 text-indigo-700", "In Bestellung"],
};
const St = ({ s }) => { const [c, l] = STATUS[s] || ["bg-gray-100", s]; return <Badge className={c}>{l}</Badge>; };

export default function PurchaseRequisitions() {
  const [rows, setRows] = useState([]);
  const [caps, setCaps] = useState([]);
  const [detail, setDetail] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const load = useCallback(() => {
    api.get("/srm/purchase-requisitions").then((r) => setRows(r.data || [])).catch((e) => toast.error(errDetail(e)));
  }, []);
  useEffect(() => { api.get("/srm/my-caps").then((r) => setCaps(r.data.caps || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const canCreate = caps.includes("pr_create");

  return (
    <div className="p-6 max-w-[1300px] mx-auto" data-testid="pr-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#1B2A4A] flex items-center justify-center"><ClipboardList className="h-5 w-5 text-[#F5A623]" /></div>
          <div><h1 className="text-2xl font-bold text-[#1B2A4A]">Bestellanforderungen</h1>
            <p className="text-sm text-gray-500">PR → Freigabe → Bestellung</p></div>
        </div>
        {canCreate && <Button data-testid="pr-new-btn" className="bg-[#1B2A4A] hover:bg-[#24365d]" onClick={() => setAddOpen(true)}><Plus className="h-4 w-4 mr-1" /> Neue Anforderung</Button>}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Nummer</TableHead><TableHead>Lieferant</TableHead><TableHead>Werk</TableHead><TableHead>Wert</TableHead><TableHead>Status</TableHead><TableHead>Angelegt</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-8">Keine Bestellanforderungen</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} className="cursor-pointer hover:bg-gray-50" data-testid={`pr-row-${r.pr_number}`} onClick={() => setDetail(r.id)}>
                <TableCell className="font-mono font-medium text-[#1B2A4A]">{r.pr_number}</TableCell>
                <TableCell>{r.supplier_name || "–"}</TableCell><TableCell>{r.plant_name || "–"}</TableCell>
                <TableCell>{fmtEur(r.total_value, r.currency)}</TableCell>
                <TableCell><St s={r.status} /></TableCell>
                <TableCell className="text-sm text-gray-500">{fmtDate(r.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {addOpen && <NewPrDialog onClose={(ch) => { setAddOpen(false); if (ch) load(); }} />}
      {detail && <PrDetailDialog id={detail} caps={caps} onClose={(ch) => { setDetail(null); if (ch) load(); }} />}
    </div>
  );
}

function NewPrDialog({ onClose }) {
  const [plants, setPlants] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState({ plant_id: "", supplier_id: "", currency: "EUR", need_by_date: "", notes: "" });
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/plants").then((r) => setPlants(r.data || [])).catch(() => {});
    api.get("/suppliers?status=approved").then((r) => setSuppliers(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (form.supplier_id) api.get(`/srm/material-sources?supplier_id=${form.supplier_id}`).then((r) => setSources(r.data || [])).catch(() => setSources([]));
    else setSources([]);
  }, [form.supplier_id]);
  const addLine = () => setLines((l) => [...l, { id: Math.random().toString(36).slice(2), article_id: "", article_name: "", qty: 1, unit_price: 0, unit: "pcs" }]);
  const setLine = (id, patch) => setLines((l) => l.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const rmLine = (id) => setLines((l) => l.filter((x) => x.id !== id));
  const pickArticle = (id, sourceId) => {
    const s = sources.find((x) => x.id === sourceId);
    if (s) setLine(id, { article_id: s.article_id, article_name: s.article_name, unit_price: s.price });
  };
  const total = lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unit_price) || 0), 0);
  const save = async () => {
    if (!form.plant_id || lines.length === 0) { toast.error("Werk und mindestens eine Position sind erforderlich"); return; }
    setBusy(true);
    try { await api.post("/srm/purchase-requisitions", { ...form, line_items: lines }); toast.success("Bestellanforderung angelegt"); onClose(true); }
    catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-2xl" data-testid="pr-new-dialog">
        <DialogHeader><DialogTitle>Neue Bestellanforderung</DialogTitle><DialogDescription>Positionen mit freigegebenem Bezugsweg des gewählten Lieferanten.</DialogDescription></DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Werk *</Label>
              <Select value={form.plant_id} onValueChange={(v) => setForm((f) => ({ ...f, plant_id: v }))}>
                <SelectTrigger data-testid="pr-plant-select"><SelectValue placeholder="Wählen…" /></SelectTrigger>
                <SelectContent>{plants.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Lieferant</Label>
              <Select value={form.supplier_id} onValueChange={(v) => setForm((f) => ({ ...f, supplier_id: v }))}>
                <SelectTrigger data-testid="pr-supplier-select"><SelectValue placeholder="Wählen…" /></SelectTrigger>
                <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="flex items-center justify-between"><Label>Positionen</Label>
            <Button size="sm" variant="outline" data-testid="pr-add-line-btn" onClick={addLine}><Plus className="h-4 w-4 mr-1" /> Position</Button></div>
          {lines.map((l) => (
            <div key={l.id} className="grid grid-cols-12 gap-2 items-end border border-gray-100 rounded-lg p-2" data-testid="pr-line">
              <div className="col-span-5"><Label className="text-xs">Artikel</Label>
                {sources.length > 0 ? (
                  <Select value={sources.find((s) => s.article_id === l.article_id)?.id || ""} onValueChange={(v) => pickArticle(l.id, v)}>
                    <SelectTrigger data-testid="pr-line-article"><SelectValue placeholder="Freigegebenen Artikel wählen…" /></SelectTrigger>
                    <SelectContent>{sources.map((s) => <SelectItem key={s.id} value={s.id}>{s.article_name}</SelectItem>)}</SelectContent></Select>
                ) : <Input placeholder="Beschreibung" value={l.article_name} onChange={(e) => setLine(l.id, { article_name: e.target.value })} />}
              </div>
              <div className="col-span-2"><Label className="text-xs">Menge</Label><Input type="number" data-testid="pr-line-qty" value={l.qty} onChange={(e) => setLine(l.id, { qty: e.target.value })} /></div>
              <div className="col-span-2"><Label className="text-xs">Preis</Label><Input type="number" data-testid="pr-line-price" value={l.unit_price} onChange={(e) => setLine(l.id, { unit_price: e.target.value })} /></div>
              <div className="col-span-2 text-sm text-gray-600 pb-2">{fmtEur((Number(l.qty) || 0) * (Number(l.unit_price) || 0))}</div>
              <div className="col-span-1"><button onClick={() => rmLine(l.id)} className="text-gray-400 hover:text-red-600 pb-2"><Trash2 className="h-4 w-4" /></button></div>
            </div>
          ))}
          <div><Label>Notiz</Label><Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></div>
          <div className="text-right font-semibold text-[#1B2A4A]" data-testid="pr-total">Summe: {fmtEur(total)}</div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onClose(false)}>Abbrechen</Button>
          <Button data-testid="pr-save-btn" disabled={busy} className="bg-[#1B2A4A] hover:bg-[#24365d]" onClick={save}>Anlegen</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PrDetailDialog({ id, caps, onClose }) {
  const [pr, setPr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [decisionNote, setDecisionNote] = useState("");
  const [justif, setJustif] = useState("");
  const load = useCallback(() => { api.get(`/srm/purchase-requisitions/${id}`).then((r) => setPr(r.data)).catch((e) => toast.error(errDetail(e))); }, [id]);
  useEffect(() => { load(); }, [load]);
  const canApprove = caps.includes("po_approve") || caps.includes("srm_admin");
  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); } };
  const submit = () => act(() => api.post(`/srm/purchase-requisitions/${id}/submit`), "Eingereicht");
  const decide = (decision) => act(() => api.post(`/srm/purchase-requisitions/${id}/decision`, { decision, note: decisionNote }), "Entscheidung gespeichert");
  const createPo = async () => {
    setBusy(true);
    try { const r = await api.post(`/srm/purchase-requisitions/${id}/create-po`, { non_approved_justification: justif }); toast.success(`Bestellung ${r.data.po_number} angelegt`); onClose(true); }
    catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  if (!pr) return null;
  const a = pr.approval || {};
  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-2xl" data-testid="pr-detail-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-mono">{pr.pr_number} <St s={pr.status} /></DialogTitle>
          <DialogDescription>{pr.supplier_name} · {pr.plant_name} · Freigabestufe {pr.approval_tier}</DialogDescription></DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          <Table><TableHeader><TableRow><TableHead>Artikel</TableHead><TableHead>Menge</TableHead><TableHead>Preis</TableHead><TableHead>Zeile</TableHead></TableRow></TableHeader>
            <TableBody>{pr.line_items.map((l) => (<TableRow key={l.id}><TableCell>{l.article_name || l.description || "–"}</TableCell><TableCell>{l.qty} {l.unit}</TableCell><TableCell>{fmtEur(l.unit_price)}</TableCell><TableCell>{fmtEur(l.line_total)}</TableCell></TableRow>))}</TableBody></Table>
          <div className="text-right font-semibold text-[#1B2A4A]">Summe: {fmtEur(pr.total_value, pr.currency)}</div>
          {a.status && <div className="text-sm bg-gray-50 rounded-lg p-3">Freigabe: <b>{a.status}</b>{a.approver_name ? ` durch ${a.approver_name}` : ""}{a.note ? ` — ${a.note}` : ""}</div>}
          {pr.status === "pending_approval" && canApprove && (
            <div className="space-y-2 border-t pt-3">
              <Label>Notiz (Pflicht bei Ablehnung)</Label>
              <Textarea data-testid="pr-decision-note" value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)} />
              <div className="flex gap-2"><Button data-testid="pr-approve-btn" disabled={busy} className="bg-green-600 hover:bg-green-700" onClick={() => decide("approved")}>Freigeben</Button>
                <Button data-testid="pr-reject-btn" disabled={busy} variant="outline" className="border-red-400 text-red-700" onClick={() => decide("rejected")}>Ablehnen</Button></div>
            </div>
          )}
          {pr.status === "approved" && canApprove && (
            <div className="space-y-2 border-t pt-3">
              <Label>Begründung (nur falls kein freigegebener Bezugsweg)</Label>
              <Input data-testid="pr-justif" value={justif} onChange={(e) => setJustif(e.target.value)} />
              <Button data-testid="pr-create-po-btn" disabled={busy} className="bg-[#1B2A4A] hover:bg-[#24365d]" onClick={createPo}>Bestellung erzeugen</Button>
            </div>
          )}
        </div>
        <DialogFooter>
          {pr.status === "draft" && <Button data-testid="pr-submit-btn" disabled={busy} className="bg-[#F5A623] hover:bg-[#e0961d] text-[#1B2A4A]" onClick={submit}>Zur Freigabe einreichen</Button>}
          <Button variant="outline" onClick={() => onClose(false)}>Schließen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
